import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { ISurveyBuilderSurvey } from "../../@types/SurveyBuilder";
import {
  ISocreStrategyOption,
  ISurveyDisplayRulesTypes,
} from "../../@types/Surveys";
import { ILanguageResponse } from "../../@types/Translations";
import {
  SurveyBuilderError,
  surveyBuilderService,
} from "../../services/surveyBuilderService";
import { surveysService } from "../../services/surveysService";
import { translationService } from "../../services/translationsService";
import { useToast } from "../useToast";

export const SURVEY_BUILDER_QUERY_KEY = "survey-builder";

export function surveyBuilderQueryKey(
  surveyId: string | undefined,
  versionId: string | undefined,
  languageCode: string
) {
  return [SURVEY_BUILDER_QUERY_KEY, surveyId, versionId ?? "", languageCode];
}

/** EN-5261: one version of a survey, with its texts in `languageCode`. */
export function useSurveyBuilder(
  surveyId: string | undefined,
  versionId: string | undefined,
  languageCode: string
) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: surveyBuilderQueryKey(surveyId, versionId, languageCode),
    queryFn: () =>
      surveyBuilderService.getSurvey(surveyId!, versionId, languageCode),
    enabled: !!surveyId,
  });

  return { survey: data, isLoading, isError, refetch };
}

/**
 * Runs one builder write (each card saves through its own endpoint). Every write answers with the
 * survey as saved, which replaces the cached version so the cards pick up their new row versions.
 * On a conflict (someone saved first) the version is reloaded; an open editor keeps what the user
 * typed, so saving again applies it on top of the newer version.
 */
export function useSurveyBuilderSave(
  surveyId: string | undefined,
  languageCode: string
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const run = useCallback(
    async (
      action: () => Promise<ISurveyBuilderSurvey>,
      successMessage?: string
    ) => {
      setIsSaving(true);
      try {
        const survey = await action();
        // Other versions' cached copies list the versions too; refetch them when opened.
        await queryClient.invalidateQueries({
          queryKey: [SURVEY_BUILDER_QUERY_KEY, survey.id],
          refetchType: "none",
        });
        queryClient.setQueryData(
          surveyBuilderQueryKey(survey.id, survey.version.id, languageCode),
          survey
        );
        if (successMessage) showToast(successMessage, "success");
        return survey;
      } catch (error) {
        if (error instanceof SurveyBuilderError && error.isConflict) {
          showToast(t("surveys_builder_errors_conflict"), "error");
          queryClient.invalidateQueries({
            queryKey: [SURVEY_BUILDER_QUERY_KEY, surveyId],
          });
        } else {
          showToast(
            (error as Error).message || t("surveys_builder_errors_saveFailed"),
            "error"
          );
        }
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient, languageCode, surveyId, showToast, t]
  );

  return { run, isSaving };
}

/**
 * The builder's fixed lists (survey types, score strategies, graph types and range comparators)
 * and every registered language: a survey can be translated into a language before the portal is.
 */
export function useSurveyBuilderOptions() {
  const { data, isLoading } = useQuery({
    queryKey: [SURVEY_BUILDER_QUERY_KEY, "options"],
    queryFn: async () => {
      const [types, strategies, displayRules, comparators, languages] =
        await Promise.all([
          surveysService.getSurveyTypesOptions(),
          surveysService.getScoreStrategiesOptions(),
          surveysService.getSurveyDisplayRules(),
          surveysService.getSurveyDisplayRulesComparators(),
          translationService.getLanguages(),
        ]);

      return {
        surveyTypes: (types.data ?? []) as string[],
        scoreStrategies: (strategies.data ?? []) as ISocreStrategyOption[],
        displayRuleTypes: (displayRules.data ??
          []) as ISurveyDisplayRulesTypes[],
        comparators: (comparators.data ?? []) as string[],
        languages: (languages.data ?? []) as ILanguageResponse[],
      };
    },
    staleTime: Infinity,
  });

  return { options: data, isOptionsLoading: isLoading };
}
